<template>
	<div
		class="text-button"
		:class="{ disabled: disabled, 'two-lines': secondText }"
		v-bind="$attrs"
	>
		<div
			class="text-button text-button--inner"
			:class="[
				$attrs.class,
				`text-button--inner--${type}`,
				{
					'text-button--inner--disabled': disabled,
					'text-button--inner--shadow': shadow,
					'text-button--inner--has-pulse': pulse,
					'two-lines': secondText,
				},
			]"
		>
			<ShrinkText
				v-if="secondText"
				:text="secondText"
				:fontSize="secondFontSize ?? fontSize"
				:isBold="fontWeight === FontWeights.BOLD ? true : false"
				:containerClassList="`text-button__text-container-second`"
			/>
			<ShrinkText
				v-if="text"
				:text="text"
				:fontSize="fontSize"
				:isBold="fontWeight === FontWeights.BOLD ? true : false"
				:containerClassList="`text-button__text-container ${timeRemaining !== null ? 'text-button__text-container--with-timer' : ''}`"
			/>
			<Timer
				class="timer"
				v-if="remainingTime !== null"
				:time="remainingTime"
				@timerFinished="remainingTime = null"
			/>
			<AppPrice
				v-if="price"
				:amount="price.value"
				:currency="price.type"
				:resourceCheck="resourceCheck"
				:fontSize="fontSize"
				:iconSize="fontSize"
			/>
			<AppNotification
				v-if="notification"
				class="text-button__notification"
				:value="notification"
				:type="StatusTypes.WARNING"
			/>
			<slot name="content" />
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppPrice from "@/components/AppPrice.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import Timer from "@/components/Timer.vue";
import { type ResourceType } from "@/game/entities/config/resources";
import { FontWeights, StatusTypes } from "@/types/ui";
import { ref } from "vue";
import AppNotification from "./AppNotification.vue";

interface Props {
	text?: string;
	secondText?: string;
	type?: StatusTypes;
	fontSize?: number;
	secondFontSize?: number;
	fontWeight?: FontWeights;
	disabled?: boolean;
	resourceCheck?: boolean;
	shadow?: boolean;
	pulse?: boolean;
	timeRemaining?: number;
	price?: { type: ResourceType; value: number };
	notification?: number | string;
}

const {
	text,
	secondText,
	secondFontSize,
	type = StatusTypes.DEFAULT,
	fontSize = 16,
	fontWeight = FontWeights.BOLD,
	disabled = false,
	resourceCheck = false,
	shadow,
	pulse = false,
	timeRemaining = null,
	price = null,
	notification = null,
} = defineProps<Props>();

const remainingTime = ref<number | null>(timeRemaining);
</script>

<style lang="scss" scoped>
.text-button {
	position: relative; // IMPORTANT FOR NOTIFICATIONs (iOS 18.6 fix)
	@include flex-row-center;
	filter: drop-shadow(0 0.1rem 0.1rem rgba(0, 0, 0, 0.5));

	cursor: pointer;
	width: 15rem;
	height: 2.313rem;
	&.two-lines {
		height: 2.813rem;
		@include flex-col-center;
		gap: 0.375rem;
	}

	&.disabled {
		cursor: not-allowed;
		pointer-events: none;
	}

	&--inner {
		width: 100%;
		@include clickable-element;
		box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
		border-width: 0.075rem;
		border-style: solid;
		border-image-source: linear-gradient(to top, #bd9032, #f0d386);
		border-image-slice: 1;

		&--default-active {
			background-image: linear-gradient(to top, #bd9032, #f0d386);
			.text-button__text-container {
				color: #372615;
				text-shadow: none !important;
			}
		}

		&--default {
			background-image: linear-gradient(to top, #181a1d, #303239);

			.text-button__text-container {
				color: #fff3dd;
			}
		}

		&--success {
			background-image: linear-gradient(to top, #1d6130, #2bad50);

			.text-button__text-container {
				color: #fff3dd;
			}
		}

		&--warning {
			background-image: linear-gradient(to top, #875806, #dd9a27);

			.text-button__text-container {
				color: #fff3dd;
			}
		}

		// orange 2 / hover
		&--info {
			background-image: linear-gradient(to top, #957335, #e1caa1);

			.text-button__text-container {
				color: #372615;
				text-shadow: none !important;
			}
		}

		&--danger {
			background-image: linear-gradient(to top, #660b20, #cd1440);

			.text-button__text-container {
				color: #fff3dd;
			}
		}

		&--primary {
			background-image: linear-gradient(to top, #1d5261, #2b91ad);

			.text-button__text-container {
				color: #fff3dd;
			}
		}

		// or premium
		&--secondary {
			background-image: linear-gradient(to top, #4e1d61, #862cad);
			text-transform: uppercase;

			.text-button__text-container {
				color: #fff3dd;
				&-second {
					position: relative;
					top: 0.15rem;
					color: #f4d278;
					font-size: 0.688rem;
				}
			}
		}

		.text-button__text-container {
			padding-bottom: 0.25rem;
			width: 100%;
			user-select: none;
			text-align: center;
			text-shadow: 0.044rem 0.044rem 0 rgba(0, 0, 0, 0.6);

			&--with-timer {
				width: unset;
				color: #f2dcb5;
			}
		}

		&--disabled {
			opacity: 0.5;
			pointer-events: none;
		}

		&--shadow {
			box-shadow:
				inset 0 -0.016rem 0.5rem 0 rgb(0 0 0 / 15%),
				0rem 0.125rem 0.25rem 0 rgb(0 0 0 / 55%);
		}
		&--has-pulse {
			animation: pulse 2.5s infinite;
		}

		.timer {
			gap: 0.3125rem;
			margin-left: 0.3125rem;
			padding-bottom: 0.25rem;

			:deep(.timer__icon) {
				margin-top: 0.1875rem;
			}
			:deep(.timer__time) {
				font-size: 1rem;
			}
		}
	}

	&__notification {
		position: absolute;
		top: -0.5rem;
		right: -0.5rem;
	}
}

:deep(.price) {
	margin-bottom: 0.4rem;
}

@keyframes pulse {
	0% {
		box-shadow: 0 0 0.2rem 0.05rem rgba($color: #feb942, $alpha: 0.8);
	}

	50% {
		box-shadow: 0 0 0.2rem 0.05rem rgba($color: #feb942, $alpha: 0);
	}
	100% {
		box-shadow: 0 0 0.2rem 0.05rem rgba($color: #feb942, $alpha: 0.8);
	}
}
</style>
